import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Copy, ExternalLink, Download, Pencil } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Modal from '@/components/ui/Modal';
import Tooltip from '@/components/ui/Tooltip';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { SITE_ID } from '@/config/site';
import { useAppMounts } from '@/lib/apps-mounts';
import { appEntryLinks } from '@/lib/app-entry-links';
import AppEntryButtons from '@/components/admin/AppEntryButtons';

interface AppRow {
  id: string;
  client_id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  base_url: string;
  redirect_uris: string[];
  scopes: string[];
  webhook_url: string | null;
  embed_origins: string[];
  is_active: boolean;
  created_at: string;
  installation_count: number;
}

// Mirrors V1_SCOPES in functions/src/routes/admin/apps.ts. Keep in sync; the
// backend validator is authoritative, this list controls the admin UI's
// registration checkboxes.
const V1_SCOPES = [
  'read_shop',
  'read_users',
  'read_subscriptions',
  'write_webhooks',
  'ai:read',
  'ai:write',
  'read_strings',
  'write_users',
  'read_app_products',
  'write_app_products',
  'read_app_purchases',
  'write_app_purchases',
  'read_orders',
  'read_products',
  'read_inventory',
  'read_customers',
  'read_carts',
  'read_reviews',
];

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  read_shop: 'Read tenant info (name, domain, currency). Safe default.',
  read_users: 'Read user accounts — ids, names, emails, roles. Required if the app needs to recognize who is using it.',
  read_subscriptions:
    'Read subscription status and plan for each user. Required if the app gates features behind a paid plan.',
  write_webhooks:
    'Register + remove webhook subscriptions (e.g., user.created, subscription.canceled). Required for real-time sync.',
  'ai:read':
    'Fetch tenant-configured AI provider credentials via /admin/v1/ai/credentials. Lets the app run AI features against your provider keys instead of bringing its own.',
  'ai:write':
    'Call /admin/v1/ai/infer, /admin/v1/ai/stream, and /admin/v1/ai/generate-image — Rumio runs the AI request server-side using tenant-configured providers. Required for any app that issues AI calls through the platform (e.g. Argus / RPAAD agents in vigil-security).',
  read_strings:
    'Read the tenant\'s localized strings (i18n namespace trees) via /admin/v1/strings. Required if the app renders translated copy on companion surfaces.',
  write_users:
    'Provision new user accounts via POST /admin/v1/users (e.g., bulk migration scripts). Idempotent on email.',
  read_app_products:
    'Read the app\'s own SKU registry on this tenant via GET /admin/v1/app-products (one-shot products / consumables).',
  write_app_products:
    'Register and update the app\'s SKU catalog via PUT /admin/v1/app-products. Required to sell one-shot products through Rumio.',
  read_app_purchases:
    'Read a user\'s one-shot purchase history for this app via GET /admin/v1/users/:id/app-purchases.',
  write_app_purchases:
    'Open Stripe Checkout sessions for one-shot purchases via POST /admin/v1/app-purchases. Required to actually charge users.',
  read_orders:
    'Read orders (line items, addresses, shipments) via GET /admin/v1/orders and subscribe to order.* webhooks. Needed by helpdesk, accounting, shipping and automation tools.',
  read_products:
    'Read the catalog via GET /admin/v1/products and subscribe to product.* webhooks.',
  read_inventory: 'Subscribe to inventory.updated webhooks (stock level changes per variant).',
  read_customers:
    'Read customers via GET /admin/v1/customers and subscribe to customer.* and newsletter.* webhooks.',
  read_carts: 'Subscribe to cart.updated webhooks (abandoned-cart tools).',
  read_reviews: 'Subscribe to review.created webhooks.',
};

interface FormState {
  name: string;
  description: string;
  base_url: string;
  redirect_uris: string; // newline-separated
  scopes: string[];
  webhook_url: string;
  embed_origins: string; // newline-separated
}

/**
 * Build the in-app OAuth consent-page URL for an app row. Rumio constructs
 * this directly instead of bouncing through the app's `/install` endpoint
 * (which used to be the flow but was a footgun — apps that forgot to
 * implement /install left admins on a 404 page). Per the documented OAuth
 * contract, the app only needs to handle the redirect_uri callback.
 *
 * `state` is generated client-side as a CSRF nonce. The app receives it
 * back on the callback but treats it as opaque — for tenant-admin-initiated
 * installs, CSRF protection comes from the admin session + the redirect_uri
 * allowlist, not from the app verifying state.
 */
function buildAuthorizeUrl(app: AppRow): string {
  const qs = new URLSearchParams({
    client_id: app.client_id,
    redirect_uri: app.redirect_uris[0],
    scope: app.scopes.join(' '),
    state: typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
    tenant: SITE_ID,
  });
  return `/admin/apps/authorize?${qs.toString()}`;
}

const INITIAL_FORM: FormState = {
  name: '',
  description: '',
  base_url: '',
  redirect_uris: '',
  scopes: [...V1_SCOPES],
  webhook_url: '',
  embed_origins: '',
};

/**
 * Rumio's convention is that apps mount their OAuth callback + webhook
 * receiver at well-known paths. We offer those as defaults the admin
 * can override, rather than making them type the same URLs three times.
 */
function deriveDefaults(baseUrl: string): { redirectUri: string; webhookUrl: string; embedOrigin: string } | null {
  const trimmed = baseUrl.trim().replace(/\/+$/, '');
  if (!trimmed) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  return {
    redirectUri: `${trimmed}/oauth/callback`,
    webhookUrl: `${trimmed}/webhooks/rumio`,
    embedOrigin: parsed.origin,
  };
}

interface TouchedFields {
  redirect_uris: boolean;
  webhook_url: boolean;
  embed_origins: boolean;
}

const INITIAL_TOUCHED: TouchedFields = {
  redirect_uris: false,
  webhook_url: false,
  embed_origins: false,
};

function LabelWithHelp({
  htmlFor,
  children,
  help,
  required,
}: {
  htmlFor: string;
  children: React.ReactNode;
  help: string;
  required?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-stone-700">
        {children}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <Tooltip content={help} />
    </div>
  );
}

const Apps = () => {
  const [apps, setApps] = useState<AppRow[]>([]);
  // The site's active mounts: where each app can actually be opened, signed in.
  const { data: mounts } = useAppMounts();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  // Tracks which derived fields the admin has manually edited — once
  // touched, we stop overwriting them as they keep typing the base URL.
  const [touched, setTouched] = useState<TouchedFields>(INITIAL_TOUCHED);
  const [saving, setSaving] = useState(false);

  // After registering, show the one-time secret in a modal
  const [secretModal, setSecretModal] = useState<null | { clientId: string; clientSecret: string; appName: string }>(null);

  // Delete confirmation. When the target has active installations we
  // require the admin to type the app name to confirm — same pattern as
  // GitHub's repo deletion gate. Reset on each new target.
  const [deleteTarget, setDeleteTarget] = useState<AppRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Scope-edit modal. Lets the admin add or remove scopes on an existing
  // app without re-registering. Note: existing installations keep their
  // previously-granted scopes — the tenant has to reinstall on each tenant
  // they want to grant the new scope.
  const [editTarget, setEditTarget] = useState<AppRow | null>(null);
  const [editScopes, setEditScopes] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editConfirmRemoval, setEditConfirmRemoval] = useState(false);

  useEffect(() => {
    void fetchApps();
  }, []);

  // Apps that finish their OAuth install send the admin back to
  // /admin/apps?installed=<installation_id>. Show a success toast and
  // strip the param so a refresh doesn't re-fire it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const installed = searchParams.get('installed');
    if (!installed) return;
    useUIStore.getState().addToast('App installed successfully.', 'success');
    const next = new URLSearchParams(searchParams);
    next.delete('installed');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchApps() {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<{ apps: AppRow[] }>('/admin/apps');
      setApps(res.apps);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  }

  function toggleScope(scope: string) {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(scope) ? f.scopes.filter((s) => s !== scope) : [...f.scopes, scope],
    }));
  }

  function handleBaseUrlChange(value: string) {
    setForm((f) => {
      const derived = deriveDefaults(value);
      return {
        ...f,
        base_url: value,
        redirect_uris: !touched.redirect_uris && derived ? derived.redirectUri : f.redirect_uris,
        webhook_url: !touched.webhook_url && derived ? derived.webhookUrl : f.webhook_url,
        embed_origins: !touched.embed_origins && derived ? derived.embedOrigin : f.embed_origins,
      };
    });
  }

  function resetForm() {
    setForm(INITIAL_FORM);
    setTouched(INITIAL_TOUCHED);
  }

  async function handleRegister() {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      base_url: form.base_url.trim(),
      redirect_uris: form.redirect_uris.split('\n').map((s) => s.trim()).filter(Boolean),
      scopes: form.scopes,
      webhook_url: form.webhook_url.trim() || null,
      embed_origins: form.embed_origins.split('\n').map((s) => s.trim()).filter(Boolean),
    };
    if (!payload.name || !payload.base_url || payload.redirect_uris.length === 0 || payload.scopes.length === 0) {
      useUIStore.getState().addToast('Name, base URL, redirect URI, and at least one scope are required', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await api.post<{ app: { id: string; client_id: string; name: string }; client_secret: string }>(
        '/admin/apps',
        payload
      );
      setRegisterOpen(false);
      resetForm();
      setSecretModal({ clientId: res.app.client_id, clientSecret: res.client_secret, appName: res.app.name });
      await fetchApps();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to register app', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    // Defense-in-depth: the button is disabled when the typed-name gate
    // fails, but re-check here in case it gets clicked anyway.
    if (deleteTarget.installation_count > 0 && deleteConfirmText !== deleteTarget.name) {
      return;
    }
    try {
      setDeleting(true);
      await api.delete(`/admin/apps/${deleteTarget.id}`);
      useUIStore.getState().addToast(`Deleted ${deleteTarget.name}`, 'success');
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await fetchApps();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to delete app', 'error');
    } finally {
      setDeleting(false);
    }
  }

  function closeDeleteModal() {
    setDeleteTarget(null);
    setDeleteConfirmText('');
  }

  function openEdit(a: AppRow) {
    setEditTarget(a);
    setEditScopes([...a.scopes]);
    setEditConfirmRemoval(false);
  }

  function closeEdit() {
    setEditTarget(null);
    setEditScopes([]);
    setEditConfirmRemoval(false);
  }

  function toggleEditScope(scope: string) {
    setEditScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  }

  async function confirmEdit() {
    if (!editTarget) return;
    if (editScopes.length === 0) {
      useUIStore.getState().addToast('At least one scope is required', 'error');
      return;
    }
    const originalSet = new Set(editTarget.scopes);
    const newSet = new Set(editScopes);
    const added = editScopes.filter((s) => !originalSet.has(s));
    const removed = editTarget.scopes.filter((s) => !newSet.has(s));
    // Map UI state → server's `propagate` field.
    //   no removals → omit (server default = "additions")
    //   removals + confirm box unchecked → "additions" (manifest-only for removals)
    //   removals + confirm box checked   → "all" (propagate removals too)
    const body: { scopes: string[]; propagate?: 'additions' | 'all' } = { scopes: editScopes };
    if (removed.length > 0 && editConfirmRemoval) body.propagate = 'all';
    else if (removed.length > 0) body.propagate = 'additions';

    try {
      setEditSaving(true);
      const res = await api.patch<{ installations_updated?: number }>(`/admin/apps/${editTarget.id}`, body);
      const count = res?.installations_updated ?? 0;
      let msg = `Updated ${editTarget.name}`;
      if (count > 0) {
        const what = added.length > 0 && (editConfirmRemoval && removed.length > 0)
          ? 'scope changes'
          : added.length > 0 ? `added scope${added.length > 1 ? 's' : ''}`
          : 'scope changes';
        msg = `Updated ${editTarget.name} — ${count} installation${count === 1 ? '' : 's'} received the ${what}`;
      }
      useUIStore.getState().addToast(msg, 'success');
      closeEdit();
      await fetchApps();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to update app', 'error');
    } finally {
      setEditSaving(false);
    }
  }

  function copy(text: string, label = 'Copied') {
    navigator.clipboard.writeText(text).then(() => useUIStore.getState().addToast(label, 'success'));
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <div role="alert" className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Apps</h1>
          <p className="text-sm text-gray-600">Third-party apps installed on your Rumio site.</p>
        </div>
        <Button onClick={() => setRegisterOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Register App
        </Button>
      </div>

      {apps.length === 0 ? (
        <Card>
          <div className="py-8 text-center text-gray-500">
            No apps registered yet. Register one to let external software plug into your site.
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Client ID</th>
                <th className="py-2 px-3">Base URL</th>
                <th className="py-2 px-3">Installs</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2 px-3">
                    <Link to={`/admin/apps/${a.id}`} className="text-blue-600 hover:underline">{a.name}</Link>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{a.client_id}</td>
                  <td className="py-2 px-3 text-xs">
                    {/* The bare origin: no frame, so no session. Kept for
                        checking the deployment is up, and labelled as such;
                        the Open buttons are the way into the app. */}
                    <a
                      href={a.base_url}
                      target="_blank"
                      rel="noreferrer"
                      title="Opens the app's server on its own, without your session. Use Open on site to use the app."
                      className="inline-flex items-center gap-1 text-gray-500 hover:underline"
                    >
                      {a.base_url} <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="py-2 px-3">{a.installation_count}</td>
                  <td className="py-2 px-3">
                    {a.is_active ? <Badge variant="green">Active</Badge> : <Badge variant="gray">Disabled</Badge>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <AppEntryButtons links={appEntryLinks(mounts, a.id)} appName={a.name} />
                      {(() => {
                        // Block the install button before the user can click
                        // it if the app is missing config that would make the
                        // OAuth flow fail downstream. The same checks live on
                        // the backend (functions/src/routes/apps/oauth.ts) —
                        // surfacing them here turns a confusing post-redirect
                        // error page into an inline tooltip.
                        const missing: string[] = [];
                        if (!a.is_active) missing.push('app is disabled');
                        if (!a.redirect_uris || a.redirect_uris.length === 0) missing.push('no redirect URIs registered');
                        if (!a.scopes || a.scopes.length === 0) missing.push('no scopes registered');
                        const canInstall = missing.length === 0;
                        const tooltip = canInstall
                          ? `Start the OAuth install flow for ${a.name} on this tenant.`
                          : `Can't install: ${missing.join(', ')}.`;
                        // Build the consent-page URL Rumio-side instead of
                        // bouncing through the app's /install endpoint. The
                        // app no longer needs to host /install — it just
                        // handles its registered redirect_uri callback. This
                        // matches the documented OAuth flow in
                        // docs/apps-platform/contract-v1.md §2.1.
                        const authorizeUrl = canInstall ? buildAuthorizeUrl(a) : '#';
                        return (
                          <Tooltip content={tooltip}>
                            {canInstall ? (
                              <a
                                href={authorizeUrl}
                                aria-label={`Install ${a.name}`}
                                className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-stone-100"
                              >
                                <Download className="w-4 h-4 text-blue-600" />
                              </a>
                            ) : (
                              <span
                                aria-disabled="true"
                                aria-label={`Install ${a.name} (unavailable)`}
                                className="inline-flex items-center justify-center w-8 h-8 rounded text-stone-300 cursor-not-allowed"
                              >
                                <Download className="w-4 h-4" />
                              </span>
                            )}
                          </Tooltip>
                        );
                      })()}
                      <Tooltip content="Edit the app's scopes. Existing installations keep what they were originally granted — admins reinstall the app to upgrade their tenants.">
                        <Button variant="ghost" onClick={() => openEdit(a)} aria-label={`Edit ${a.name}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </Tooltip>
                      <Button variant="ghost" onClick={() => setDeleteTarget(a)} aria-label={`Delete ${a.name}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Register modal */}
      <Modal isOpen={registerOpen} onClose={() => { setRegisterOpen(false); resetForm(); }} title="Register an App">
        <div className="space-y-4">
          <div>
            <LabelWithHelp
              htmlFor="app-name"
              required
              help="The app's display name. Shown to admins on /admin/apps and to users on the OAuth consent screen. You'll see this wherever Rumio lists the app."
            >
              Name
            </LabelWithHelp>
            <Input
              id="app-name"
              placeholder="Meal Planner"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div>
            <LabelWithHelp
              htmlFor="app-description"
              help="One-sentence summary shown on the OAuth consent screen. Optional, but makes the install flow friendlier."
            >
              Description
            </LabelWithHelp>
            <Textarea
              id="app-description"
              placeholder="AI-powered meal planning, ingredient tracking, and grocery list generation."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div>
            <LabelWithHelp
              htmlFor="app-base-url"
              required
              help="The app's public HTTPS origin. Rumio uses this to derive the redirect URI, webhook URL, and embed origin below — you only need to type it once."
            >
              Base URL
            </LabelWithHelp>
            <Input
              id="app-base-url"
              placeholder="https://your-app.example.com"
              value={form.base_url}
              onChange={(e) => handleBaseUrlChange(e.target.value)}
              required
            />
          </div>

          <div>
            <LabelWithHelp
              htmlFor="app-redirect-uris"
              required
              help="Where Rumio sends the user back after they approve the OAuth install. Auto-filled with Rumio's convention ({base_url}/oauth/callback). Override if your app mounts the callback elsewhere. One per line."
            >
              Redirect URIs <span className="text-stone-400 font-normal">(auto-filled)</span>
            </LabelWithHelp>
            <Textarea
              id="app-redirect-uris"
              placeholder="https://your-app.example.com/oauth/callback"
              value={form.redirect_uris}
              onChange={(e) => { setTouched((t) => ({ ...t, redirect_uris: true })); setForm({ ...form, redirect_uris: e.target.value }); }}
              required
            />
          </div>

          <div>
            <LabelWithHelp
              htmlFor="app-webhook-url"
              help="Where Rumio POSTs signed webhook events (user.created, subscription.canceled, etc.). Auto-filled with {base_url}/webhooks/rumio. Leave blank if the app doesn't need webhooks."
            >
              Webhook URL <span className="text-stone-400 font-normal">(auto-filled)</span>
            </LabelWithHelp>
            <Input
              id="app-webhook-url"
              placeholder="https://your-app.example.com/webhooks/rumio"
              value={form.webhook_url}
              onChange={(e) => { setTouched((t) => ({ ...t, webhook_url: true })); setForm({ ...form, webhook_url: e.target.value }); }}
            />
          </div>

          <div>
            <LabelWithHelp
              htmlFor="app-embed-origins"
              help="Origins allowed to embed the app in an iframe on Rumio pages (admin + public). Enforced via CSP frame-ancestors. Auto-filled with the base URL's origin. One per line."
            >
              Embed Origins <span className="text-stone-400 font-normal">(auto-filled)</span>
            </LabelWithHelp>
            <Textarea
              id="app-embed-origins"
              placeholder="https://your-app.example.com"
              value={form.embed_origins}
              onChange={(e) => { setTouched((t) => ({ ...t, embed_origins: true })); setForm({ ...form, embed_origins: e.target.value }); }}
            />
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="block text-sm font-medium text-stone-700">
                Scopes<span className="text-red-500 ml-0.5">*</span>
              </span>
              <Tooltip content="The permissions this app gets on your tenant. The user sees these on the consent screen. Grant the minimum the app needs — you can revoke later by uninstalling." />
            </div>
            <div className="space-y-1.5">
              {V1_SCOPES.map((scope) => (
                <div key={scope} className="flex items-center gap-2 text-sm">
                  <input
                    id={`scope-${scope}`}
                    type="checkbox"
                    checked={form.scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                    aria-label={scope}
                  />
                  <label htmlFor={`scope-${scope}`} className="font-mono cursor-pointer">
                    {scope}
                  </label>
                  <Tooltip content={SCOPE_DESCRIPTIONS[scope]} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setRegisterOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleRegister} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Register App'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* One-time secret modal */}
      <Modal isOpen={!!secretModal} onClose={() => setSecretModal(null)} title="App registered — save your secret now">
        {secretModal && (
          <div className="space-y-3">
            <p className="text-sm text-red-700">
              This is the <strong>only</strong> time your client_secret will be shown. Copy it now and store it securely
              — Rumio cannot recover it later. You can rotate it from the app detail page if you lose it.
            </p>
            <div>
              <label className="block text-sm font-medium">Client ID</label>
              <div className="flex gap-2 items-center">
                <code className="flex-1 bg-gray-100 px-2 py-1 rounded text-sm">{secretModal.clientId}</code>
                <Button variant="ghost" onClick={() => copy(secretModal.clientId, 'Client ID copied')} aria-label="Copy client_id">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium">Client Secret</label>
              <div className="flex gap-2 items-center">
                <code className="flex-1 bg-gray-100 px-2 py-1 rounded text-sm break-all">{secretModal.clientSecret}</code>
                <Button variant="ghost" onClick={() => copy(secretModal.clientSecret, 'Client secret copied')} aria-label="Copy client_secret">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => setSecretModal(null)}>I've saved it</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit scopes */}
      <Modal isOpen={!!editTarget} onClose={closeEdit} title={`Edit ${editTarget?.name ?? ''} — Scopes`}>
        {editTarget && (() => {
          const originalSet = new Set(editTarget.scopes);
          const newSet = new Set(editScopes);
          const added = editScopes.filter((s) => !originalSet.has(s));
          const removed = editTarget.scopes.filter((s) => !newSet.has(s));
          const installCount = editTarget.installation_count ?? 0;
          const hasDiff = added.length > 0 || removed.length > 0;
          return (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Added scopes propagate to existing installations immediately —
              no reinstall needed. Removed scopes only propagate when you opt
              in below, since pulling a scope from a running app can break it.
            </p>
            <div className="space-y-2">
              {V1_SCOPES.map((scope) => (
                <label key={scope} className="flex items-start gap-2 p-2 rounded hover:bg-stone-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editScopes.includes(scope)}
                    onChange={() => toggleEditScope(scope)}
                    className="mt-1"
                  />
                  <div className="text-sm">
                    <div className="font-mono">{scope}</div>
                    <div className="text-xs text-stone-600">{SCOPE_DESCRIPTIONS[scope]}</div>
                  </div>
                </label>
              ))}
            </div>

            {hasDiff && (
              <div className="rounded border border-stone-200 bg-stone-50 p-3 text-sm space-y-1">
                <div>
                  <span className="font-medium text-stone-700">Adding:</span>{' '}
                  {added.length > 0
                    ? added.map((s) => <code key={s} className="mr-2 text-emerald-700">{s}</code>)
                    : <span className="text-stone-500">(none)</span>}
                </div>
                <div>
                  <span className="font-medium text-stone-700">Removing:</span>{' '}
                  {removed.length > 0
                    ? removed.map((s) => <code key={s} className="mr-2 text-red-700">{s}</code>)
                    : <span className="text-stone-500">(none)</span>}
                </div>
                {added.length > 0 && installCount > 0 && (
                  <div className="text-xs text-stone-600 pt-1">
                    {installCount} installation{installCount === 1 ? '' : 's'} will gain the added scope{added.length === 1 ? '' : 's'} immediately. No re-install required.
                  </div>
                )}
              </div>
            )}

            {removed.length > 0 && (
              <label className="flex items-start gap-2 text-sm p-2 rounded bg-amber-50 border border-amber-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editConfirmRemoval}
                  onChange={(e) => setEditConfirmRemoval(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  Also remove these scopes from existing installations.{' '}
                  <span className="text-amber-800">Installed apps may stop working until they're updated.</span>
                </span>
              </label>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={closeEdit} disabled={editSaving}>
                Cancel
              </Button>
              <Button onClick={confirmEdit} disabled={editSaving || editScopes.length === 0}>
                {editSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save scopes
              </Button>
            </div>
          </div>
          );
        })()}
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteTarget} onClose={closeDeleteModal} title={`Delete ${deleteTarget?.name ?? ''}?`}>
        {deleteTarget && (() => {
          const hasInstalls = deleteTarget.installation_count > 0;
          const typedNameOk = deleteConfirmText === deleteTarget.name;
          const canConfirm = !deleting && (!hasInstalls || typedNameOk);
          return (
            <div className="space-y-4">
              <p className="text-sm">
                This permanently deletes the app registration and{' '}
                {hasInstalls ? (
                  <strong className="text-red-700">
                    its {deleteTarget.installation_count}{' '}
                    {deleteTarget.installation_count === 1 ? 'active installation' : 'active installations'}, all access tokens, and the entire webhook delivery history
                  </strong>
                ) : (
                  'all of its associated data (installations, access tokens, webhook subscriptions, and delivery history)'
                )}
                . The client_id and client_secret are revoked immediately. This cannot be undone.
              </p>

              {hasInstalls && (
                <div>
                  <label htmlFor="delete-confirm-name" className="block text-sm font-medium mb-1">
                    To confirm, type <code className="bg-gray-100 px-1 py-0.5 rounded">{deleteTarget.name}</code> below.
                  </label>
                  <Input
                    id="delete-confirm-name"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    autoComplete="off"
                    autoFocus
                    aria-label={`Type ${deleteTarget.name} to confirm`}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={closeDeleteModal}>Cancel</Button>
                <Button onClick={confirmDelete} disabled={!canConfirm}>
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <><Trash2 className="w-4 h-4 mr-2" /> Delete app</>
                  )}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default Apps;
